const { chunk, orderBy, range } = require('lodash');
const { format: formatDate } = require('date-fns');
const numeral = require('numeral');

const { getCollectionData } = require('./firebase');

const fieldDisplayValue = (v, { type, options, displayValue, initialValue, } = {}) => {
  if(type == null) return null;
  if(displayValue) return displayValue(v);

  return (({
    password: _ => v ? '********' : '',
    select: _ => (options.find(_ => _.value === v) || {}).label,
    multiSelect: _ => (v || []).map(v => (options.find(_ => _.value === v) || {}).label).join(', '),
    date: _ => v && formatDate(v.toDate(), 'yyyy/MM/dd'),
    datetime: _ => v && formatDate(v.toDate(), 'yyyy/MM/dd HH:mm'),
    boolean: _ => ({ true: 'YES', false: 'NO' })[v || initialValue],
  })[type] || (_ => v))();
};

const postalCodeWithHyphen = _ => _.slice(0,3) + '-' + _.slice(3);

const idWithTenant = ref => [ref.parent.parent.id, ref.id].join('-');

const newId = (lastItem) => {
  return ((lastItem ? parseInt(lastItem.id.replace(/\D/g, ''), 10) : 0) + 1).toString();
};

const unslash = _ => _?.replace(/\//g, '%%slash%%');
const slash = _ => _?.replace(/%%slash%%/g, '/');

const chunkSerial = async (xs, n, f) => {
  await chunk(xs, n).reduce(async (_, xs) => {
    await _;

    await Promise.all(xs.map((x, i) => f(x, i)));
  });
};

const serial = async (xs, f) => {
  return await xs.reduce(async (_, x, i) => {
    return [...(await _), await f(x, i)];
  }, Promise.resolve([]));
};

// 採番の空き探索の上限。カウンタが実データからずれていても現実的な回数で収束させる。
const shippingRequestIdMaxProbe = 100;

const getNextShippingRequestId = async (tenantRef, db) => {
  // トランザクションでatomicにインクリメント
  return await db.runTransaction(async (transaction) => {
    const doc = await transaction.get(tenantRef);
    const currentValue = doc.data().shippingRequestIdCounter || 0;

    // カウンタが実データより遅れていると既存ドキュメントのIDを返してしまい、
    // 呼び出し側の set(merge: true) が既存の出荷依頼を上書きしてしまう。
    // 空いているIDが見つかるまで進める(通常は1回で確定するため追加の読み取りは発生しない)。
    const findVacantNumber = async (candidate, probe = 0) => {
      if (probe >= shippingRequestIdMaxProbe) {
        throw new Error(`出荷依頼IDの空きが見つかりません (S${currentValue + 1}〜S${currentValue + shippingRequestIdMaxProbe})`);
      }
      const existing = await transaction.get(tenantRef.collection('shippingRequests').doc('S' + candidate));
      return existing.exists ? findVacantNumber(candidate + 1, probe + 1) : candidate;
    };
    const newValue = await findVacantNumber(currentValue + 1);

    transaction.update(tenantRef, { shippingRequestIdCounter: newValue });
    return 'S' + newValue;
  });
};

// CSVインポートのように多数を一度に採番する用途。
// 1件ずつトランザクションを回すと件数分の往復が直列に積み上がるため、まとめて確保する。
const getNextShippingRequestIds = async (tenantRef, db, count) => {
  if (count < 1) return [];

  const shippingRequestsRef = tenantRef.collection('shippingRequests');
  const vacantNumbersIn = async (numbers) => {
    const founds = await serial(chunk(numbers, 100), async (numbers) => {
      const docs = await Promise.all(numbers.map(_ => shippingRequestsRef.doc('S' + _).get()));
      return numbers.filter((_, i) => !docs[i].exists);
    });
    return founds.flat();
  };

  // カウンタが実データより遅れていると既存ドキュメントのIDを返してしまい、
  // 呼び出し側の set が既存の出荷依頼を上書きしてしまう。
  // 埋まっていた分は次の範囲を追加で確保して、必要数の空きが揃うまで繰り返す。
  const collect = async (found = [], probe = 0) => {
    if (found.length >= count) return found.slice(0, count);
    if (probe >= shippingRequestIdMaxProbe) {
      throw new Error('出荷依頼IDの空きが見つかりません');
    }

    const shortage = count - found.length;
    const { start, end } = await db.runTransaction(async (transaction) => {
      const doc = await transaction.get(tenantRef);
      const currentValue = doc.data().shippingRequestIdCounter || 0;

      // トランザクション内で候補を全件readすると、書き込まないドキュメント分の
      // verifyがcommitに載り上限(500)に達するため、ここでは範囲を確保するだけにする。
      transaction.update(tenantRef, { shippingRequestIdCounter: currentValue + shortage });
      return { start: currentValue + 1, end: currentValue + shortage };
    });

    return await collect([...found, ...(await vacantNumbersIn(range(start, end + 1)))], probe + 1);
  };

  return (await collect()).map(_ => 'S' + _);
};

module.exports = {
  fieldDisplayValue,
  postalCodeWithHyphen,
  idWithTenant,
  newId,
  unslash,
  slash,
  chunkSerial,
  serial,
  getNextShippingRequestId,
  getNextShippingRequestIds,
};
